import React, { useMemo, useState } from 'react';
import { Box, Flex, Heading, IconButton, SimpleGrid, Skeleton, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { ArrowLeft, Buildings, ChartLineUp, CurrencyDollar, Warning } from 'phosphor-react';
import { GlassCard, GlassPageBg } from '../../../../../components/glass';
import { SearchModeBar, SearchModeOption } from '../../../../../components/SearchModeBar';
import { useContaCilEmpresasSaude } from '../../../../../hooks/useContaCilEmpresas';
import { EmpresaBuscaModo } from '../../../../../hooks/useContaCilImportar';
import { fmtValor } from '../conciliar/shared';
import { MiniKpiRow } from '../plano-contas/ui';
import { EmpresaSaudeCard } from './components/EmpresaSaudeCard';

type Filtro = 'todas' | 'pendencias' | 'emdia';

const SEARCH_MODES: SearchModeOption[] = [
  {
    key: 'razao',
    buttonLabel: 'Razão',
    menuLabel: 'Razão social',
    placeholder: 'Digite a razão social…',
  },
  { key: 'cnpj', buttonLabel: 'CNPJ', menuLabel: 'CNPJ', placeholder: 'Digite o CNPJ…' },
  {
    key: 'id',
    buttonLabel: 'Id',
    menuLabel: 'Id (external_id)',
    placeholder: 'Digite o id (external_id)…',
  },
];

const FILTROS: { id: Filtro; label: string }[] = [
  { id: 'todas', label: 'Todas' },
  { id: 'pendencias', label: 'Com pendências' },
  { id: 'emdia', label: 'Em dia' },
];

const SKELETON_KEYS = ['a', 'b', 'c', 'd', 'e', 'f'];

export function EmpresasLista({
  onSelecionar,
  onVoltar,
}: {
  onSelecionar: (empresaId: number) => void;
  onVoltar: () => void;
}) {
  const { data, isLoading } = useContaCilEmpresasSaude();
  const [busca, setBusca] = useState('');
  const [buscaModo, setBuscaModo] = useState<EmpresaBuscaModo>('razao');
  const [filtro, setFiltro] = useState<Filtro>('todas');

  const itens = useMemo(() => {
    let arr = data?.itens ?? [];
    if (filtro === 'pendencias') arr = arr.filter(i => i.pendentes > 0);
    else if (filtro === 'emdia') arr = arr.filter(i => i.pendentes === 0);
    const termo = busca.trim();
    if (termo) {
      if (buscaModo === 'cnpj') {
        const dig = termo.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
        if (dig) {
          arr = arr.filter(i =>
            (i.cnpj || '')
              .replace(/[^0-9A-Za-z]/g, '')
              .toUpperCase()
              .includes(dig),
          );
        }
      } else if (buscaModo === 'id') {
        // Match exato: digitar "1" só casa external_id "1", não 12/1203/...
        arr = arr.filter(i => (i.external_id || '') === termo);
      } else {
        const q = termo.toLowerCase();
        arr = arr.filter(i => (i.razao_social || i.nome || '').toLowerCase().includes(q));
      }
    }
    return arr;
  }, [data, filtro, busca, buscaModo]);

  const kpis = useMemo(
    () => [
      {
        label: 'Empresas',
        valor: String(data?.total_empresas ?? 0),
        icon: <Buildings size={16} weight="duotone" />,
        accent: '#0EA5E9',
      },
      {
        label: '% conciliado médio',
        valor: `${data?.percent_medio ?? 0}%`,
        icon: <ChartLineUp size={16} weight="duotone" />,
        accent: '#10B981',
      },
      {
        label: 'R$ pendente total',
        valor: `R$ ${fmtValor(data?.valor_pendente_total ?? 0)}`,
        icon: <CurrencyDollar size={16} weight="duotone" />,
        accent: '#F59E0B',
      },
      {
        label: 'Contas sem POC',
        valor: String(data?.contas_sem_poc_total ?? 0),
        icon: <Warning size={16} weight="duotone" />,
        accent: '#EF4444',
      },
    ],
    [data],
  );

  const renderConteudo = () => {
    if (isLoading) {
      return (
        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
          {SKELETON_KEYS.map(k => (
            <Skeleton key={k} height="118px" borderRadius="24px" />
          ))}
        </SimpleGrid>
      );
    }
    if (itens.length === 0) {
      return (
        <GlassCard intensity="medium" p={10}>
          <Text fontSize="sm" color="#64748B" textAlign="center">
            {data?.itens?.length
              ? 'Nenhuma empresa corresponde ao filtro.'
              : 'Nenhuma empresa com lançamentos ainda. Importe extratos para começar.'}
          </Text>
        </GlassCard>
      );
    }
    return (
      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
        {itens.map(item => (
          <EmpresaSaudeCard
            key={item.empresa_id}
            item={item}
            onClick={() => onSelecionar(item.empresa_id)}
          />
        ))}
      </SimpleGrid>
    );
  };

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
        >
          <Flex align="center" gap={3} mb={5}>
            <IconButton
              aria-label="Voltar"
              icon={<ArrowLeft size={18} weight="bold" />}
              variant="ghost"
              size="sm"
              borderRadius="full"
              color="#64748B"
              _hover={{ bg: 'rgba(15, 23, 42, 0.06)', color: '#0F172A' }}
              onClick={onVoltar}
            />
            <Box
              w="64px"
              h="64px"
              borderRadius="2xl"
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)"
              color="#0EA5E9"
              boxShadow="0 8px 24px -4px rgba(14, 165, 233, 0.32)"
            >
              <Buildings size={34} weight="duotone" />
            </Box>
            <Box>
              <Text
                fontSize="11px"
                color="#94A3B8"
                textTransform="uppercase"
                letterSpacing="0.1em"
                fontWeight="semibold"
              >
                Conciliação Contábil
              </Text>
              <Heading fontSize={{ base: '24px', md: '32px' }} fontWeight="700" color="#0F172A">
                Empresas
              </Heading>
              <Text fontSize="sm" color="#64748B">
                Saúde da conciliação por empresa — clique para abrir o painel da empresa.
              </Text>
            </Box>
          </Flex>
        </motion.div>

        <MiniKpiRow kpis={kpis} />

        {/* Busca + filtros */}
        <Flex gap={3} mb={5} wrap="wrap" align="center">
          {/* Sem wrapper de coluna aqui. O SearchModeBar declara
              `flex="0 1 480px"`, escrito assumindo pai em LINHA — ou seja,
              largura. Dentro de um `direction="column"` o eixo principal vira
              vertical e os 480px viravam ALTURA: o conteudo tem 38px e o
              componente e `align="center"`, entao sobravam ~221px de vazio
              acima e abaixo do filtro. A largura vai direto na prop `maxW`,
              que o componente ja aceita. */}
          <SearchModeBar
            modes={SEARCH_MODES}
            mode={buscaModo}
            onModeChange={m => setBuscaModo(m as EmpresaBuscaModo)}
            value={busca}
            onValueChange={setBusca}
            maxW={{ base: '100%', md: '380px' }}
          />
          <Flex
            display="inline-flex"
            gap="2px"
            p="4px"
            borderRadius="full"
            bg="rgba(255, 255, 255, 0.72)"
            backdropFilter="blur(20px) saturate(180%)"
            sx={{ WebkitBackdropFilter: 'blur(20px) saturate(180%)' }}
            borderWidth="1px"
            borderColor="rgba(255, 255, 255, 0.6)"
          >
            {FILTROS.map(f => {
              const on = filtro === f.id;
              return (
                <Box
                  key={f.id}
                  as="button"
                  onClick={() => setFiltro(f.id)}
                  px={4}
                  py={1.5}
                  borderRadius="full"
                  fontSize="12px"
                  fontWeight="600"
                  whiteSpace="nowrap"
                  color={on ? '#0EA5E9' : '#64748B'}
                  bg={on ? 'white' : 'transparent'}
                  boxShadow={on ? '0 2px 8px -2px rgba(15, 23, 42, 0.12)' : 'none'}
                  transition="all 0.2s"
                  _hover={on ? {} : { color: '#0F172A' }}
                >
                  {f.label}
                </Box>
              );
            })}
          </Flex>
        </Flex>

        {/* Lista */}
        {renderConteudo()}
      </Box>
    </GlassPageBg>
  );
}
